import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import {
  getStudents, getMessages, createMessage, cancelMessage, getWaGroups,
  type WaGroup,
} from '../../lib/api'
import { useContactSearch } from '../../lib/useContactSearch'
import { useContactName } from '../../lib/useContactName'
import { keys } from '../../lib/queryKeys'
import { PageHeader } from '../../components/PageHeader'
import { Entry, EntryList } from '../../components/Entry'
import { StatusBadge } from '../../components/StatusBadge'
import { normalizePhone, prettyPhone, waLink } from '../../lib/phone'
import { m } from '../../paraglide/messages'
import type { Student, ScheduledMessage } from '../../types'

export const Route = createFileRoute('/messages/')({
  // ?student=<id> pre-selects that student in the composer (from the Students list).
  validateSearch: (s: Record<string, unknown>): { student?: number } => {
    const id = Number(s.student)
    return Number.isFinite(id) && id > 0 ? { student: id } : {}
  },
  component: MessagesPage,
})

const routeApi = getRouteApi('/messages/')

const fmtDT = new Intl.DateTimeFormat('he-IL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Jerusalem' })
function fdt(iso: string | null | undefined) {
  if (!iso) return '—'
  return fmtDT.format(new Date(iso))
}

function MessagesPage() {
  return (
    <div>
      <PageHeader title={m.messages_title()} />
      <Composer />
      <Scheduled />
    </div>
  )
}

// ─── Composer ─────────────────────────────────────────────────────────────────

type RecipientState = {
  type: 'student' | 'contact' | 'group' | 'number'
  phone?: string
  groupId?: string
  name: string
}

function Composer() {
  const queryClient = useQueryClient()
  const { student: preselectId } = routeApi.useSearch()

  const [recipient, setRecipient] = useState<RecipientState | null>(null)
  const [search, setSearch]       = useState('')
  const [message, setMessage]     = useState('')
  const [fireAt, setFireAt]       = useState('')
  const [error, setError]         = useState('')

  // Always-on contact search — auto-pulls from Google if the cache is empty.
  const { results: contacts, searching, pulling } = useContactSearch(search)

  const { data: students } = useQuery<Student[]>({
    queryKey: keys.students,
    queryFn:  getStudents,
  })

  // Always fetch groups on mount so results are instant when the user searches.
  const { data: groups } = useQuery<WaGroup[]>({
    queryKey: ['wa-groups'],
    queryFn:  getWaGroups,
    staleTime: 60_000,
    retry: false,
  })

  // Honor ?student=<id>: pre-select that student once the list has loaded.
  useEffect(() => {
    if (!preselectId || !students) return
    const s = students.find((s) => s.id === preselectId)
    if (!s) return
    setRecipient({ type: 'student', phone: s.whatsappPhone ?? '', name: s.name })
  }, [preselectId, students])

  const mutation = useMutation({
    mutationFn: createMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.messages })
      setRecipient(null); setSearch('')
      setMessage(''); setFireAt(''); setError('')
    },
    onError: (err) => setError((err as Error).message),
  })

  const submit = () => {
    setError('')
    if (recipient?.type === 'group') {
      if (!recipient.groupId) { setError(m.messages_err_group()); return }
    } else {
      if (!recipient?.phone) { setError(m.messages_err_recipient()); return }
    }
    if (!message) { setError(m.messages_err_body()); return }
    if (!fireAt)  { setError(m.messages_err_when()); return }

    if (recipient.type === 'group') {
      mutation.mutate({
        groupId: recipient.groupId!,
        groupName: recipient.name,
        message,
        fireAt: new Date(fireAt).toISOString(),
      })
    } else {
      mutation.mutate({
        phone:  normalizePhone(recipient.phone!),
        message,
        fireAt: new Date(fireAt).toISOString(),
      })
    }
  }

  // Open a live WhatsApp chat now; groups don't support wa.me links.
  const startWhatsApp = () => {
    setError('')
    if (!recipient?.phone) { setError(m.messages_err_recipient()); return }
    const link = waLink(normalizePhone(recipient.phone))
    if (!link)  { setError(m.messages_err_number()); return }
    const url = message ? `${link}?text=${encodeURIComponent(message)}` : link
    window.open(url, '_blank', 'noopener')
  }

  // Dropdown results
  const q = search.trim()
  const filteredStudents = q
    ? (students ?? []).filter(s => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, 5)
    : []
  const filteredContacts = (contacts ?? []).slice(0, 5)
  const filteredGroups = q
    ? (groups ?? []).filter(g => g.name.toLowerCase().includes(q.toLowerCase())).slice(0, 5)
    : []
  const hasResults = filteredStudents.length > 0 || filteredContacts.length > 0 || filteredGroups.length > 0
  const showPhoneRow = /^[0-9+\s\-()]{6,}$/.test(q) && !hasResults

  const fieldCls = 'border border-zinc-300 bg-transparent px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900'
  const rowCls   = 'flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-zinc-50 border-b border-zinc-100 last:border-0'
  const sectionHeaderCls = 'px-3 py-1 text-xs text-zinc-400 uppercase tracking-wide bg-zinc-50'

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit() }}
      className="flex flex-col gap-3 border border-zinc-200 p-4 mb-8 max-w-xl"
    >
      {/* Unified recipient search */}
      <div className="flex flex-col gap-1">
        {recipient ? (
          <div className="flex items-center gap-2 border border-zinc-300 px-2 py-1.5 text-sm">
            <span className="flex-1 truncate">{recipient.name}</span>
            <button
              type="button"
              onClick={() => { setRecipient(null); setSearch('') }}
              className="text-zinc-400 hover:text-zinc-900 shrink-0 leading-none"
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder={m.messages_recipient_placeholder()}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              className={fieldCls}
            />
            {q.length > 0 && (
              <div className="border border-zinc-200 max-h-56 overflow-y-auto">
                {pulling ? (
                  <p className="px-3 py-2 text-xs text-zinc-400">{m.messages_syncing()}</p>
                ) : (
                  <>
                    {filteredStudents.length > 0 && (
                      <>
                        <p className={sectionHeaderCls}>{m.messages_mode_student()}</p>
                        {filteredStudents.map(s => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => { setRecipient({ type: 'student', phone: s.whatsappPhone ?? '', name: s.name }); setSearch('') }}
                            className={rowCls}
                          >
                            <span className="truncate">{s.name}</span>
                            {s.whatsappPhone && <span className="text-xs text-zinc-400 shrink-0">{prettyPhone(s.whatsappPhone)}</span>}
                          </button>
                        ))}
                      </>
                    )}
                    {filteredContacts.length > 0 && (
                      <>
                        <p className={sectionHeaderCls}>{m.messages_mode_contact()}</p>
                        {filteredContacts.map((g, i) => (
                          <button
                            key={`${g.phone ?? ''}-${i}`}
                            type="button"
                            onClick={() => { setRecipient({ type: 'contact', phone: g.phone ?? '', name: g.name }); setSearch('') }}
                            className={rowCls}
                          >
                            <span className="truncate">{g.name}</span>
                            <span className="text-xs text-zinc-400 shrink-0">{g.phone ? prettyPhone(g.phone) : m.messages_no_number()}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {filteredGroups.length > 0 && (
                      <>
                        <p className={sectionHeaderCls}>{m.messages_mode_group()}</p>
                        {filteredGroups.map(g => (
                          <button
                            key={g.id}
                            type="button"
                            onClick={() => { setRecipient({ type: 'group', groupId: g.id, name: g.name }); setSearch('') }}
                            className={rowCls}
                          >
                            <span className="truncate">{g.name}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {showPhoneRow && (
                      <button
                        type="button"
                        onClick={() => { setRecipient({ type: 'number', phone: q, name: q }); setSearch('') }}
                        className="flex w-full items-center px-3 py-2 text-start text-sm hover:bg-zinc-50"
                      >
                        {m.messages_send_to_number({ phone: prettyPhone(q) })}
                      </button>
                    )}
                    {!hasResults && !showPhoneRow && (
                      <p className="px-3 py-2 text-xs text-zinc-400">
                        {searching ? m.messages_searching() : m.messages_no_matches()}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Message */}
      <textarea
        rows={4}
        placeholder={m.messages_body_placeholder()}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className={fieldCls}
      />

      {/* When to send — scheduling is the whole point here */}
      <label className="flex flex-col gap-1">
        <span className="text-sm text-zinc-700">{m.messages_send_at()}</span>
        <input
          type="datetime-local"
          value={fireAt}
          onChange={(e) => setFireAt(e.target.value)}
          className={fieldCls}
        />
      </label>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {/* Two headline actions: open a live WhatsApp chat now, or schedule for later (#20). */}
      <div className="flex flex-wrap gap-2">
        {recipient?.type !== 'group' && (
          <button
            type="button"
            onClick={startWhatsApp}
            className="flex-1 border border-zinc-900 px-4 py-2 text-sm hover:bg-zinc-100"
          >
            {m.messages_start_whatsapp()}
          </button>
        )}
        <button
          type="submit"
          disabled={mutation.isPending}
          className="flex-1 bg-zinc-900 text-white px-4 py-2 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? m.messages_scheduling() : m.messages_schedule()}
        </button>
      </div>
    </form>
  )
}

// ─── Scheduled list ───────────────────────────────────────────────────────────

function Scheduled() {
  const queryClient = useQueryClient()
  const resolveName = useContactName()
  const [expanded, setExpanded] = useState(false)

  const { data, isLoading, error } = useQuery<ScheduledMessage[]>({
    queryKey: keys.messages,
    queryFn:  getMessages,
  })

  const cancel = useMutation({
    mutationFn: (id: number) => cancelMessage(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.messages }),
  })

  if (isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (error) return <p className="text-red-600 text-sm">{(error as Error).message}</p>

  const messages = data ?? []
  const pending  = messages.filter((msg) => msg.status === 'pending')
  const visible  = expanded ? messages : messages.slice(0, 3)
  const hidden   = messages.length - visible.length

  return (
    <div>
      <h2 className="text-xs text-zinc-400 uppercase tracking-wide mb-2">
        {m.messages_recent({ count: pending.length })}
      </h2>
      <EntryList isEmpty={messages.length === 0} emptyMessage={m.messages_empty()}>
        {visible.map((msg) => (
          <Entry
            key={msg.id}
            title={
              msg.groupId
                ? (msg.groupName ?? msg.groupId)
                : resolveName(msg.phone)
            }
            meta={msg.message}
            submeta={fdt(msg.fireAt)}
            status={<StatusBadge status={msg.status} />}
            actions={
              msg.status === 'pending' ? (
                <button
                  onClick={() => cancel.mutate(msg.id)}
                  disabled={cancel.isPending}
                  className="border border-red-700 text-red-700 px-2 py-1 text-xs hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {m.action_cancel()}
                </button>
              ) : undefined
            }
          />
        ))}
      </EntryList>

      {messages.length > 3 && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-3 text-sm text-zinc-500 hover:text-zinc-900"
        >
          {expanded ? m.messages_show_less() : m.messages_show_more({ count: hidden })}
        </button>
      )}
    </div>
  )
}
